/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useEffect, useRef } from 'react';
import { Category } from '@/features/category/pages/Category';
import { usePageType } from '@/features/category/api/getPageType';
import { useMappedCategoryData } from '@/features/category/api/mapCategoryData';
import { useRouter } from 'next/router';
import { setCookie } from '@/utils/cookie';
import { clearProductFilterSelection } from '../../actions/shelf/shelf';
import { ProductShelf } from '@/features/shelf/components/ShelfDetails';
import TwoColumnSkeleton from '../Skeletons/TwoColumn';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useScrollToRef } from '@/hooks/useScrollToRef';
import { useDispatch } from '@/hooks/redux/useDispatch';
import usePrevious from '@/utils/usePrevious';
import { SisterStoreProvider } from '@/features/fulfillment/context/SisterStoreProvider';

function RoutingContainer() {
  const router = useRouter();
  const justMounted = useRef(true);
  const shelfRef = useRef<HTMLDivElement>(null);
  const preferredVehicle = usePreferredVehicle();
  const previousPreferredVehicleId = usePrevious(preferredVehicle?.catalogVehicleId);
  const dispatch = useDispatch();
  const { status } = useMappedCategoryData({ statusOnly: true });
  useEffect(() => {
    justMounted.current = false;
  }, []);

  useScrollToRef({
    ref: shelfRef,
    conditions:
      previousPreferredVehicleId !== preferredVehicle?.catalogVehicleId && !justMounted.current,
    dependencies: [preferredVehicle?.catalogVehicleId, previousPreferredVehicleId],
    scrollOffset: 200,
  });

  const {
    data: pageTypeData,
    isFetching: pageTypeIsFetching,
    isLoading: pageTypeIsLoading,
  } = usePageType();

  const redirectPath = pageTypeData?.redirectLocation;

  useEffect(() => {
    if (
      (status === 'error' || status === 'success') &&
      !pageTypeIsLoading &&
      !pageTypeIsFetching &&
      !pageTypeData?.pageType &&
      !redirectPath
    ) {
      router.replace('/errorPage');
    } else if (redirectPath) {
      router.replace(redirectPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTypeData, redirectPath, status]);

  useEffect(() => {
    return () => {
      dispatch(clearProductFilterSelection());
    };
  }, [dispatch]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setCookie('prevPageType', pageTypeData?.pageType || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTypeData]);

  switch (pageTypeData?.pageType) {
    case 'ProductShelf':
      return (
        <>
          <SisterStoreProvider>
            <ProductShelf shelfRef={shelfRef} />
          </SisterStoreProvider>
        </>
      );

    case 'CategoryGrouping':
    case 'LeafCategory':
    case 'TopNavCategory':
    case 'category':
      return <Category />;

    default:
  }

  return <TwoColumnSkeleton />;
}

export default RoutingContainer;
